// CASSETTA_DOC_v2 — cassetta unica dei documenti in arrivo (fatture XML, PDF, foto)
import React, { useState, useEffect, useRef } from "react";
import { senzaDoppiStipendi, meseDoc, dataCosto } from "./StatisticheAzienda"; // STAT_AZIENDA_v1 MESE_DOC_v1
import BancaMovimenti from "./BancaMovimenti"; // BANCA_v1
import PrezziBolle from "./PrezziBolle"; // PREZZI_BOLLE_v1
import FattureVendita from "./FattureVendita"; // VENDITE_v1

const C = {
  bg: "#0f1117", surface: "#1a1d27", surface2: "#21253a", border: "#2a2e45", accent: "#f5a623",
  text: "#e8eaf2", muted: "#7b82a0", dim: "#4a5068", ok: "#3ecf8e", warn: "#f5a623", err: "#f06060", info: "#4e9af5",
};
const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const DEST = { generale: "Spese generali", cantiere: "Cantiere", lavoratore: "Lavoratore", magazzino: "Magazzino", bolle: "Secondo le bolle (più cantieri)" };
const TIPI = ["Fattura", "Fattura differita", "Nota di credito", "Bolletta", "Quietanza polizza", "Polizza", "F24", "Busta paga", "Cassa edile", "Bonifico", "Contratto", "Preventivo fornitore", "Ricevuta", "Scontrino", "Estratto conto", "Documento", "Altro"];
const NON_COSTO = ["IVA versata (non è un costo)", "Documenti (senza spesa)"];
const MESI_LUNGHI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
function meseIt(m) { return /^\d{4}-\d{2}$/.test(m || "") ? MESI_LUNGHI[parseInt(m.slice(5, 7), 10) - 1] + " " + m.slice(0, 4) : (m || ""); }
function dataPag(s) {
  const t = String(s || "");
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/); if (m) return m[1] + "-" + m[2] + "-" + m[3];
  m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/); if (m) return m[3] + "-" + m[2].padStart(2, "0") + "-" + m[1].padStart(2, "0");
  return "";
}

function tok() { try { return localStorage.getItem("bertoc_jwt") || ""; } catch (e) { return ""; } }
async function api(url, opts) {
  const o = opts || {};
  const h = { Authorization: "Bearer " + tok() };
  if (o.body && !(o.body instanceof FormData)) h["Content-Type"] = "application/json";
  const r = await fetch(url, { method: o.method || "GET", headers: h, body: o.body ? (o.body instanceof FormData ? o.body : JSON.stringify(o.body)) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(j.error || ("Errore " + r.status)); e.status = r.status; e.dati = j; throw e; }
  return j;
}
function euro(n) {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "—";
  return "€ " + Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function dataIt(s) { return /^\d{4}-\d{2}-\d{2}/.test(s || "") ? s.slice(8, 10) + "/" + s.slice(5, 7) + "/" + s.slice(0, 4) : (s || ""); }
function numDa(v) { if (v === "" || v === null || v === undefined) return null; const n = parseFloat(String(v).replace(",", ".")); return isNaN(n) ? null : n; }
const MODALITA = { MP01: "contanti", MP02: "assegno", MP05: "bonifico", MP08: "carta", MP12: "RIBA", MP15: "giroconto", MP16: "domiciliazione", MP19: "SEPA", MP21: "SEPA" };

// Le foto del telefono vengono rimpicciolite prima di partire
async function preparaFile(f) {
  if (!/^image\//.test(f.type || "")) return f;
  try {
    const u = URL.createObjectURL(f);
    const img = await new Promise((ok, ko) => { const i = new Image(); i.onload = () => ok(i); i.onerror = ko; i.src = u; });
    const max = 2200; let w = img.naturalWidth, h = img.naturalHeight;
    if (Math.max(w, h) > max) { const r = max / Math.max(w, h); w = Math.round(w * r); h = Math.round(h * r); }
    const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
    cv.getContext("2d").drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(u);
    const b = await new Promise(ok => cv.toBlob(ok, "image/jpeg", 0.85));
    return new File([b], (f.name || "foto").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch (e) { return f; }
}

const st = {
  card: { background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: 16, marginBottom: 14 },
  lab: { fontSize: 11, color: C.muted, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 },
  inp: { width: "100%", boxSizing: "border-box", padding: "9px 10px", fontSize: 15, borderRadius: 8, border: "1px solid " + C.border, background: C.bg, color: C.text, outline: "none", fontFamily: "inherit" },
  btn: (tipo) => ({ padding: "9px 14px", borderRadius: 9, border: "1px solid " + (tipo === "primario" ? C.accent : C.border), background: tipo === "primario" ? C.accent : C.surface, color: tipo === "primario" ? "#1a1d27" : C.text, fontSize: 14, fontWeight: 700, cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }),
};

function Campo({ label, children, largo }) {
  return <div style={{ gridColumn: largo ? "1 / -1" : "auto", minWidth: 0 }}><div style={st.lab}>{label}</div>{children}</div>;
}

function SchedaAttesa({ s, categorie, cantieri, lavoratori, segnale, onFatto }) {
  const d0 = s.dati || {}, p = s.proposta || {};
  const [d, setD] = useState({
    tipoDoc: d0.tipoDoc || "", fornitore: d0.fornitore || "", piva: d0.piva || "", numero: d0.numero || "", data: d0.data || "",
    imponibile: d0.imponibile ?? "", iva: d0.iva ?? "", totale: d0.totale ?? "", descrizione: d0.descrizione || "",
    periodoDa: d0.periodoDa || "", periodoA: d0.periodoA || "", pagato: !!d0.pagato,
  });
  const [categoria, setCategoria] = useState(p.categoria || "Altro");
  const [destinazione, setDestinazione] = useState(p.destinazione || "generale");
  const [cantiereId, setCantiereId] = useState(p.cantiereId || "");
  const [lavoratoreId, setLavoratoreId] = useState(p.lavoratoreId || "");
  const [mese, setMese] = useState(d0.mese || (d0.data || "").slice(0, 7));
  const [note, setNote] = useState("");
  const [creaScad, setCreaScad] = useState(false);
  const [lavoro, setLavoro] = useState(false);
  const xml = d0.origine === "xml";
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));
  const scadenze = d0.scadenze || [];

  async function conferma(forza, auto) {
    if (destinazione === "cantiere" && !cantiereId) { if (auto) return onFatto("salta", { msg: "manca il cantiere" }); alert("Scegli il cantiere."); return; }
    if (destinazione === "lavoratore" && !lavoratoreId) { if (auto) return onFatto("salta", { msg: "manca il lavoratore" }); alert("Scegli il lavoratore."); return; }
    if (auto && p.doppione) return onFatto("salta", { msg: "possibile doppione" });
    setLavoro(true);
    try {
      const dati = { ...d, imponibile: numDa(d.imponibile), iva: numDa(d.iva), totale: numDa(d.totale) };
      const j = await api("/api/cassetta/attesa/" + s.id + "/conferma", { method: "POST", body: { dati, categoria, destinazione, cantiereId, lavoratoreId, mese, note, creaScadenze: creaScad, forza: !!forza } });
      onFatto("ok", j);
    } catch (e) {
      if (auto) { setLavoro(false); return onFatto("salta", { msg: e.message }); }
      if (e.status === 409 && window.confirm(e.message + "\n\nRegistrarlo lo stesso?")) { setLavoro(false); return conferma(true); }
      alert("Non riesco ad archiviare: " + e.message);
    }
    setLavoro(false);
  }
  const segnaleIniziale = useRef(segnale);
  useEffect(() => { if (segnale && segnale !== segnaleIniziale.current) conferma(false, true); }, [segnale]);
  async function scarta() {
    if (!window.confirm("Scartare questo documento? Il file caricato viene tolto dalla cassetta.")) return;
    try { await api("/api/cassetta/attesa/" + s.id, { method: "DELETE" }); onFatto("scartato"); } catch (e) { alert(e.message); }
  }

  const ordCantieri = cantieri.slice().sort((a, b) => (!!a.completato - !!b.completato) || String(a.nome || "").localeCompare(String(b.nome || "")));
  return (
    <div style={{ ...st.card, borderColor: p.doppione ? C.err : (xml ? C.border : C.info + "88") }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: xml ? C.ok + "22" : C.info + "22", color: xml ? C.ok : C.info }}>
          {xml ? "🧾 Fattura elettronica · dati esatti" : "✨ Letto dall'IA · controlla i numeri"}
        </span>
        {s.parteDi > 1 && s.pagine && s.pagine.length > 0 && <span style={{ fontSize: 12, color: C.accent }}>pag. {s.pagine.join(", ")} di {s.pagineTot}</span>}
        <span style={{ flex: 1, fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.nomeFile}</span>
        {s.fileUrl && <a href={s.fileUrl} target="_blank" rel="noopener noreferrer" style={st.btn()}>👁 Apri</a>}
      </div>

      {s.soloDati && <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Si registrano solo i dati: la fattura resta conservata su Tieni il Conto.</div>}
      {p.doppione && (
        <div style={{ background: C.err + "1a", border: "1px solid " + C.err, color: C.err, borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
          ⚠️ Sembra già registrato: n. {p.doppione.numero} del {dataIt(p.doppione.data)} ({euro(p.doppione.totale)}). Se è un doppione premi «Scarta».
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
        <Campo label="Fornitore" largo><input style={st.inp} value={d.fornitore} onChange={e => set("fornitore", e.target.value)} /></Campo>
        <Campo label="Tipo">
          <select style={st.inp} value={TIPI.includes(d.tipoDoc) ? d.tipoDoc : "__"} onChange={e => set("tipoDoc", e.target.value === "__" ? d.tipoDoc : e.target.value)}>
            {!TIPI.includes(d.tipoDoc) && <option value="__">{d.tipoDoc || "—"}</option>}
            {TIPI.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Campo>
        <Campo label="Numero"><input style={st.inp} value={d.numero} onChange={e => set("numero", e.target.value)} /></Campo>
        <Campo label="Data"><input type="date" style={st.inp} value={d.data} onChange={e => set("data", e.target.value)} /></Campo>
        <Campo label="P.IVA"><input style={st.inp} value={d.piva} onChange={e => set("piva", e.target.value)} /></Campo>
        <Campo label="Imponibile"><input style={st.inp} inputMode="decimal" value={d.imponibile} onChange={e => set("imponibile", e.target.value)} /></Campo>
        <Campo label="IVA"><input style={st.inp} inputMode="decimal" value={d.iva} onChange={e => set("iva", e.target.value)} /></Campo>
        <Campo label="Totale"><input style={{ ...st.inp, fontWeight: 700 }} inputMode="decimal" value={d.totale} onChange={e => set("totale", e.target.value)} /></Campo>
        {(!xml || d.periodoDa) && <Campo label="Periodo dal"><input type="date" style={st.inp} value={d.periodoDa} onChange={e => set("periodoDa", e.target.value)} /></Campo>}
        {(!xml || d.periodoA) && <Campo label="al"><input type="date" style={st.inp} value={d.periodoA} onChange={e => set("periodoA", e.target.value)} /></Campo>}
        <Campo label="Descrizione" largo><input style={st.inp} value={d.descrizione} onChange={e => set("descrizione", e.target.value)} /></Campo>
      </div>
      {(d0.netto != null || d0.oreOrdinarie != null) && (
        <div style={{ marginTop: 10, fontSize: 13, color: C.muted }}>
          {d0.oreOrdinarie != null ? "Ore ordinarie " + d0.oreOrdinarie : ""}{d0.oreStraordinarie ? " · straordinarie " + d0.oreStraordinarie : ""}
          {d0.lordo != null ? " · lordo " + euro(d0.lordo) : ""}{d0.netto != null ? " · netto " + euro(d0.netto) : ""}
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 13 }}>
        {scadenze.length > 0 ? (
          <div>
            <div style={st.lab}>Pagamento</div>
            {scadenze.map((x, i) => <div key={i}>📅 {dataIt(x.data)} · {euro(x.importo)}{x.modalita ? " · " + (MODALITA[x.modalita] || x.modalita) : ""}</div>)}
            {!d.pagato && (
              <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={creaScad} onChange={e => setCreaScad(e.target.checked)} style={{ width: 18, height: 18, accentColor: C.accent }} />
                Aggiungi allo scadenzario
              </label>
            )}
          </div>
        ) : null}
        <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, cursor: "pointer", color: C.muted }}>
          <input type="checkbox" checked={d.pagato} onChange={e => set("pagato", e.target.checked)} style={{ width: 18, height: 18, accentColor: C.ok }} />
          Già pagato
        </label>
      </div>

      {(p.collegamenti && p.collegamenti.length > 0) || (p.ddtMancanti && p.ddtMancanti.length > 0) ? (
        <div style={{ marginTop: 12, background: C.bg, borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>🚚 DDT della fattura: {(p.ddtTrovati || 0) + (p.ddtMancanti || []).length} · trovati nelle bolle {p.ddtTrovati || 0}</div>
          {(p.collegamenti || []).map((c, i) => <div key={i} style={{ color: C.muted }}>→ {c.nome}: {c.bolle} {c.bolle === 1 ? "bolla" : "bolle"}{c.importo ? " · " + euro(c.importo) + " nelle bolle" : ""}</div>)}
          {(p.ddtMancanti || []).length > 0 && <div style={{ color: C.warn, marginTop: 4 }}>Non trovati nelle bolle: {p.ddtMancanti.slice(0, 12).join(", ")}{p.ddtMancanti.length > 12 ? " … e altri " + (p.ddtMancanti.length - 12) : ""}</div>}
          <div style={{ color: C.dim, fontSize: 12, marginTop: 4 }}>I materiali delle bolle sono già nei costi dei cantieri: nel riepilogo questa fattura non viene contata due volte.</div>
        </div>
      ) : null}

      {(d0.righe || []).length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ fontSize: 13, color: C.muted, cursor: "pointer" }}>Vedi le {d0.righe.length} righe</summary>
          <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 6, fontSize: 12 }}>
            {d0.righe.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 8, padding: "4px 0", borderBottom: "1px solid " + C.border + "55" }}>
                <span style={{ flex: 1 }}>{r.desc}</span>
                <span style={{ color: C.muted, whiteSpace: "nowrap" }}>{r.qta != null ? r.qta + " " + (r.um || "") : ""}</span>
                <span style={{ whiteSpace: "nowrap", minWidth: 80, textAlign: "right" }}>{euro(r.totale)}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginTop: 14, paddingTop: 12, borderTop: "1px solid " + C.border }}>
        <Campo label="Dove va">
          <select style={st.inp} value={destinazione} onChange={e => setDestinazione(e.target.value)}>
            {Object.keys(DEST).filter(k => k !== "bolle" || (p.collegamenti && p.collegamenti.length > 0) || destinazione === "bolle").map(k => <option key={k} value={k}>{DEST[k]}</option>)}
          </select>
        </Campo>
        {destinazione === "lavoratore" && (
          <Campo label="Lavoratore">
            <select style={st.inp} value={lavoratoreId} onChange={e => setLavoratoreId(e.target.value)}>
              <option value="">— scegli —</option>
              {lavoratori.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          </Campo>
        )}
        {destinazione === "lavoratore" && (
          <Campo label="Mese"><input type="month" style={st.inp} value={mese} onChange={e => setMese(e.target.value)} /></Campo>
        )}
        {destinazione === "cantiere" && (
          <Campo label="Cantiere">
            <select style={st.inp} value={cantiereId} onChange={e => setCantiereId(e.target.value)}>
              <option value="">— scegli —</option>
              {ordCantieri.map(c => <option key={c._id || c.id} value={c._id || c.id}>{(c.completato ? "✓ " : "") + (c.nome || "")}</option>)}
            </select>
          </Campo>
        )}
        <Campo label="Categoria">
          <select style={st.inp} value={categoria} onChange={e => setCategoria(e.target.value)}>
            {categorie.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Campo>
        <Campo label="Note" largo><input style={st.inp} value={note} onChange={e => setNote(e.target.value)} placeholder="facoltative" /></Campo>
      </div>
      {(d0.lavoratoreCF || d0.lavoratoreNome) && !p.lavoratoreTrovato && <div style={{ fontSize: 12, color: C.warn, marginTop: 6 }}>Non ho trovato «{d0.lavoratoreNome || d0.lavoratoreCF}» tra i Lavoratori: sceglilo tu o controlla il codice fiscale nella sua scheda.</div>}
      {p.memoria && <div style={{ fontSize: 12, color: C.dim, marginTop: 6 }}>Ultima volta questo fornitore: {p.memoria.categoria} · {DEST[p.memoria.destinazione] || p.memoria.destinazione}</div>}

      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <button style={st.btn("primario")} disabled={lavoro} onClick={() => conferma(false)}>{lavoro ? "Registro…" : (s.soloDati ? "✓ Conferma e registra" : "✓ Conferma e archivia")}</button>
        <button style={{ ...st.btn(), color: C.muted }} disabled={lavoro} onClick={scarta}>Scarta</button>
      </div>
    </div>
  );
}

function FinestraInvio({ docs, preset, onClose }) {
  const [per, setPer] = useState(preset && preset.per || "");
  const [nota, setNota] = useState(preset && preset.nota || "");
  const [giorni, setGiorni] = useState(preset && preset.giorni || 3);
  const [lavoro, setLavoro] = useState(false);
  const [fatto, setFatto] = useState(null);
  const sensibili = docs.filter(x => x.destinazione === "lavoratore" || /busta|stipend/i.test((x.tipo || "") + " " + (x.categoria || "")));
  async function crea() {
    if (!per.trim()) { alert("Scrivi per chi sono (es. Commercialista, oppure il nome del lavoratore)."); return; }
    if (sensibili.length && !(preset && preset.lavoratore) && !window.confirm("Ci sono " + sensibili.length + " documenti con dati personali di dipendenti (buste paga, stipendi). Mandarli a «" + per + "»?")) return;
    setLavoro(true);
    try { setFatto(await api("/api/cassetta/invia", { method: "POST", body: { ids: docs.map(x => x.id), per: per.trim(), nota: nota.trim(), giorni } })); }
    catch (e) { alert("Non riesco a creare il link: " + e.message); }
    setLavoro(false);
  }
  const testo = fatto ? ("Buongiorno, ecco i documenti di BERTOC PROJECT SRL:\n" + fatto.url + "\n\nIl link resta attivo fino al " + new Date(fatto.scade_il).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) + ".") : "";
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 14 }}>
      <div style={{ ...st.card, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", marginBottom: 0 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ flex: 1, fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 17 }}>📤 Invia {docs.length} {docs.length === 1 ? "documento" : "documenti"}</div>
          <button onClick={onClose} style={{ ...st.btn(), padding: "5px 10px" }}>✕</button>
        </div>
        <ul style={{ margin: "0 0 12px 18px", padding: 0, fontSize: 13, color: C.muted, maxHeight: 140, overflowY: "auto" }}>
          {docs.map(x => <li key={x.id}>{(x.lavoratoreNome && x.destinazione === "lavoratore" ? x.lavoratoreNome + " · " : "") + (x.descrizione || x.tipo) + (x.data ? " · " + dataIt(x.data) : "")}</li>)}
        </ul>
        {!fatto && (
          <div>
            <div style={st.lab}>Per chi</div>
            <input style={st.inp} value={per} onChange={e => setPer(e.target.value)} placeholder="Es. Studio commercialista" />
            <div style={{ ...st.lab, marginTop: 10 }}>Messaggio (facoltativo)</div>
            <textarea style={{ ...st.inp, minHeight: 56 }} value={nota} onChange={e => setNota(e.target.value)} />
            <div style={{ ...st.lab, marginTop: 10 }}>Il link resta attivo</div>
            <div style={{ display: "flex", gap: 8 }}>{[1, 3, 7].map(g => <button key={g} onClick={() => setGiorni(g)} style={{ ...st.btn(), borderColor: giorni === g ? C.accent : C.border, color: giorni === g ? C.accent : C.text }}>{g === 1 ? "1 giorno" : g + " giorni"}</button>)}</div>
            <div style={{ marginTop: 14 }}><button style={st.btn("primario")} disabled={lavoro} onClick={crea}>{lavoro ? "Creo…" : "Crea link"}</button></div>
          </div>
        )}
        {fatto && (
          <div>
            <div style={{ color: C.ok, fontWeight: 700, marginBottom: 6 }}>✓ Link pronto ({fatto.n} documenti)</div>
            {fatto.saltati && fatto.saltati.length > 0 && <div style={{ fontSize: 12, color: C.warn, marginBottom: 6 }}>Non inclusi (fatture elettroniche, su Tieni il Conto): {fatto.saltati.length}</div>}
            <div style={{ fontSize: 13, padding: "8px 10px", background: C.bg, borderRadius: 8, wordBreak: "break-all", marginBottom: 10 }}>{fatto.url}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {typeof navigator !== "undefined" && navigator.share && <button style={st.btn("primario")} onClick={() => navigator.share({ title: "Documenti BERTOC PROJECT", text: testo }).catch(() => {})}>📤 Condividi</button>}
              <a style={st.btn()} href={"https://wa.me/?text=" + encodeURIComponent(testo)} target="_blank" rel="noopener noreferrer">WhatsApp</a>
              <a style={st.btn()} href={"mailto:?subject=" + encodeURIComponent("Documenti BERTOC PROJECT SRL") + "&body=" + encodeURIComponent(testo)}>✉️ Email</a>
              <button style={st.btn()} onClick={() => { try { navigator.clipboard.writeText(fatto.url); alert("Link copiato"); } catch (e) { window.prompt("Copia il link:", fatto.url); } }}>📋 Copia</button>
            </div>
            <div style={{ fontSize: 12, color: C.dim, marginTop: 10 }}>Lo trovi anche in Sicurezza → 📤 Invia documenti → Inviati, dove puoi disattivarlo.</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Cassetta({ cantieriDB = [] }) {
  const [tab, setTab] = useState("arrivo");
  const [attesa, setAttesa] = useState([]);
  const [documenti, setDocumenti] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [lavoratori, setLavoratori] = useState([]);
  const [pagamenti, setPagamenti] = useState([]);
  const [coda, setCoda] = useState(null);
  const [errori, setErrori] = useState([]);
  const [sopra, setSopra] = useState(false);
  const [anno, setAnno] = useState(String(new Date().getFullYear()));
  const [filtroCat, setFiltroCat] = useState("");
  const [filtroMese, setFiltroMese] = useState("");
  const [cerca, setCerca] = useState("");
  const [avviso, setAvviso] = useState("");
  const [segnale, setSegnale] = useState(0);
  const [scelti, setScelti] = useState({});
  const [invio, setInvio] = useState(null);
  const [prezzi, setPrezzi] = useState(null);
  const [daPrezzare, setDaPrezzare] = useState([]);
  const inputRef = useRef(null);

  async function ricarica() {
    try {
      const a = await api("/api/cassetta/attesa");
      setAttesa(a.attesa || []); setCategorie(a.categorie || []);
      const d = await api("/api/cassetta/documenti");
      setDocumenti(d.documenti || []);
    } catch (e) { setErrori(x => x.concat("Caricamento: " + e.message)); }
    try {
      const l = await api("/api/lavoratori");
      setLavoratori((l.lavoratori || []).filter(x => x && x.attivo !== false).map(x => ({ id: x.id, nome: (x.nome_completo || ((x.nome || "") + " " + (x.cognome || ""))).trim() })).sort((a, b) => a.nome.localeCompare(b.nome)));
    } catch (e) {}
    try { const pg = await api("/api/v2/pagamenti"); setPagamenti(Array.isArray(pg) ? pg : []); } catch (e) {}
  }
  useEffect(() => { ricarica(); }, []);

  async function carica(lista) {
    const files = Array.from(lista || []);
    if (!files.length) return;
    setTab("arrivo"); setErrori([]);
    let vendNuove = 0, vendGia = 0; // VENDITE_v1
    for (let i = 0; i < files.length; i++) {
      setCoda({ n: i + 1, tot: files.length, nome: files[i].name });
      try {
        const f = await preparaFile(files[i]);
        const fd = new FormData(); fd.append("file", f, f.name);
        const j = await api("/api/cassetta/leggi", { method: "POST", body: fd });
        setAttesa(a => a.concat(j.schede || []));
        if (j.vendita) { if (j.vendita.nuova) vendNuove++; else vendGia++; } // VENDITE_v1
      } catch (e) { setErrori(x => x.concat(files[i].name + ": " + e.message)); }
    }
    setCoda(null);
    if (vendNuove || vendGia) { setAvviso("✓ Fatture di vendita: " + vendNuove + " registrate" + (vendGia ? ", " + vendGia + " già presenti" : "") + ". Le trovi in 🧾 Vendite."); setTimeout(() => setAvviso(""), 6000); } // VENDITE_v1
  }

  function fatto(id, esito, j) {
    if (esito === "salta") { setErrori(x => x.concat("Non confermato automaticamente: " + (j && j.msg))); return; }
    setAttesa(a => a.filter(x => x.id !== id));
    if (esito === "ok") {
      if (j && j.ddt) setDaPrezzare(q => q.concat([{ id: j.id, nome: j.nome || "fattura" }]));
      setAvviso(j.soloDati ? "✓ Registrato (solo dati)" : "✓ Archiviato in " + j.cartella + (j.scadenze ? " · " + j.scadenze + " scadenze aggiunte" : ""));
      setTimeout(() => setAvviso(""), 4000);
      api("/api/cassetta/documenti").then(d => setDocumenti(d.documenti || [])).catch(() => {});
    }
  }

  // CASSETTA_MASSA_v1: conferma e scarto in blocco, una scheda alla volta
  const [chiedi, setChiedi] = useState("");
  const [massa, setMassa] = useState(null);
  const nDop = attesa.filter(s => s.proposta && s.proposta.doppione).length;
  const pronta = s => { const p = s.proposta || {}; const dest = p.destinazione || "generale"; return !p.doppione && !(dest === "cantiere" && !p.cantiereId) && !(dest === "lavoratore" && !p.lavoratoreId); };
  async function confermaInBlocco() {
    setChiedi(""); setErrori([]);
    const lista = attesa.filter(pronta);
    let fatte = 0;
    const conDdt = [];
    for (let i = 0; i < lista.length; i++) {
      const s = lista[i], p = s.proposta || {}, d0 = s.dati || {};
      setMassa({ testo: "Registro", n: i + 1, tot: lista.length });
      try {
        const j = await api("/api/cassetta/attesa/" + s.id + "/conferma", { method: "POST", body: {
          dati: {}, categoria: p.categoria || "Altro", destinazione: p.destinazione || "generale",
          cantiereId: p.cantiereId || "", lavoratoreId: p.lavoratoreId || "",
          mese: d0.mese || String(d0.data || "").slice(0, 7), note: "", creaScadenze: false, forza: false } });
        fatte++;
        setAttesa(a => a.filter(x => x.id !== s.id));
        if (j && j.ddt) conDdt.push({ id: j.id, nome: j.nome || "fattura" });
      } catch (e) { setErrori(x => x.concat((d0.fornitore || s.nomeFile) + ": " + e.message)); }
    }
    setMassa(null);
    if (conDdt.length) setDaPrezzare(q => q.concat(conDdt));
    setAvviso("✓ Registrate " + fatte + " schede. Restano da guardare a mano quelle doppie o senza cantiere / lavoratore.");
    api("/api/cassetta/documenti").then(d => setDocumenti(d.documenti || [])).catch(() => {});
  }
  async function scartaDoppioni() {
    setChiedi("");
    const lista = attesa.filter(s => s.proposta && s.proposta.doppione);
    let fatte = 0;
    for (let i = 0; i < lista.length; i++) {
      setMassa({ testo: "Scarto", n: i + 1, tot: lista.length });
      try { await api("/api/cassetta/attesa/" + lista[i].id, { method: "DELETE" }); fatte++; setAttesa(a => a.filter(x => x.id !== lista[i].id)); }
      catch (e) { setErrori(x => x.concat(lista[i].nomeFile + ": " + e.message)); }
    }
    setMassa(null);
    setAvviso("✓ Scartati " + fatte + " doppioni");
  }
  function confermaTutte() {
    if (!window.confirm("Confermare tutte le " + attesa.length + " schede così come sono proposte?\n\nQuelle senza cantiere o lavoratore scelto, o che sembrano doppioni, restano da confermare a mano.")) return;
    setErrori([]); setSegnale(n => n + 1);
  }

  const anni = Array.from(new Set(documenti.map(x => String(x.data || "").slice(0, 4)).filter(Boolean).concat(String(new Date().getFullYear())))).sort().reverse();
  const nomeCant = id => { const c = cantieriDB.find(x => (x._id || x.id) === id); return c ? c.nome : ""; };
  const filtrati = documenti.filter(x => (filtroMese ? meseDoc(x) === anno + "-" + filtroMese : String(x.data || "").startsWith(anno)) && (!filtroCat || x.categoria === filtroCat)
    && (!cerca.trim() || [x.fornitore, x.numero, x.descrizione, x.cantiere, x.lavoratoreNome, x.tipo].join(" ").toLowerCase().includes(cerca.trim().toLowerCase())));
  const valore = x => (x.imponibile !== null && x.imponibile !== undefined ? Number(x.imponibile) : Number(x.totale || 0));
  const giaNeiCantieri = x => x.categoria === "Materiali cantiere" && (x.destinazione === "bolle" || (x.collegamenti || []).length > 0);
  const listaScelti = documenti.filter(x => scelti[x.id]);

  async function cambiaCategoria(x, cat) {
    try { await api("/api/cassetta/documenti/" + x.id, { method: "PATCH", body: { categoria: cat } }); setDocumenti(ds => ds.map(y => y.id === x.id ? { ...y, categoria: cat } : y)); }
    catch (e) { alert(e.message); }
  }
  async function elimina(x) {
    if (!window.confirm("Eliminare dall'archivio " + (x.lavoratoreNome || x.fornitore) + (x.numero ? " n. " + x.numero : "") + "?\n\n" + (x.files && x.files.length ? "Il file non viene perso: va nel cestino della cassetta." : "Si tolgono solo i dati registrati.") + (x.scadenze && x.scadenze.length ? "\nSe l'avevi messo nello scadenzario, toglilo anche da lì." : ""))) return;
    try { await api("/api/cassetta/documenti/" + x.id, { method: "DELETE" }); setDocumenti(ds => ds.filter(y => y.id !== x.id)); }
    catch (e) { alert(e.message); }
  }

  // riepilogo categoria x mese (senza IVA)
  const righeRiep = {};
  senzaDoppiStipendi(documenti).filter(x => dataCosto(x).startsWith(anno)).forEach(x => {
    const m = parseInt(dataCosto(x).slice(5, 7), 10) - 1;
    const k = x.categoria || "Altro";
    righeRiep[k] = righeRiep[k] || { mesi: Array(12).fill(0), tot: 0, doppio: 0 };
    const v = valore(x);
    if (m >= 0 && m < 12) righeRiep[k].mesi[m] += v;
    righeRiep[k].tot += v;
    if (giaNeiCantieri(x)) righeRiep[k].doppio += v;
  });
  const catCosti = Object.keys(righeRiep).filter(k => !NON_COSTO.includes(k)).sort((a, b) => righeRiep[b].tot - righeRiep[a].tot);
  const catAltre = Object.keys(righeRiep).filter(k => NON_COSTO.includes(k));
  const totMesi = Array(12).fill(0).map((_, m) => catCosti.reduce((s, k) => s + righeRiep[k].mesi[m], 0));
  const totAnno = totMesi.reduce((s, v) => s + v, 0);
  const totGenerali = catCosti.reduce((s, k) => s + righeRiep[k].tot - righeRiep[k].doppio, 0);
  const incMesi = Array(12).fill(0);
  pagamenti.forEach(p => { const dt = dataPag(p.data); if (dt.startsWith(anno)) incMesi[parseInt(dt.slice(5, 7), 10) - 1] += Number(p.importo) || 0; });
  const incAnno = incMesi.reduce((s, v) => s + v, 0);
  const n0 = v => v ? Math.round(v).toLocaleString("it-IT") : "·";

  const tb = (id, label) => <button onClick={() => setTab(id)} style={{ ...st.btn(), background: tab === id ? "#f5a62322" : C.surface, borderColor: tab === id ? C.accent : C.border, color: tab === id ? C.accent : C.text }}>{label}</button>;
  const td = { textAlign: "right", padding: "7px 6px" };

  return (
    <div className="content" style={{ maxWidth: 1000, margin: "0 auto", padding: "0 8px", color: C.text }}>
      <div onDragOver={e => { e.preventDefault(); setSopra(true); }} onDragLeave={() => setSopra(false)}
        onDrop={e => { e.preventDefault(); setSopra(false); carica(e.dataTransfer.files); }}
        onClick={() => inputRef.current && inputRef.current.click()}
        style={{ ...st.card, textAlign: "center", padding: "26px 16px", cursor: "pointer", borderStyle: "dashed", borderWidth: 2, borderColor: sopra ? C.accent : C.border, background: sopra ? "#f5a62312" : C.surface }}>
        <div style={{ fontSize: 30 }}>📥</div>
        <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 17, marginTop: 4 }}>Trascina qui i documenti o tocca per sceglierli</div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>Fatture XML/P7M da Tieni il Conto (solo dati, lettura esatta) · PDF e foto: F24, buste paga, bonifici, polizze, contratti… (li legge l'IA, anche più documenti in un PDF)</div>
        <input ref={inputRef} type="file" multiple accept=".xml,.p7m,.pdf,image/*" style={{ display: "none" }} onChange={e => { carica(e.target.files); e.target.value = ""; }} />
      </div>

      {coda && <div style={{ ...st.card, borderColor: C.accent }}>⏳ Leggo {coda.n} di {coda.tot}: <b>{coda.nome}</b>…</div>}
      {avviso && <div style={{ ...st.card, borderColor: C.ok, color: C.ok }}>{avviso}</div>}
      {daPrezzare.length > 0 && <div style={{ ...st.card, borderColor: C.accent }}><div style={{ fontWeight: 700, marginBottom: 6 }}>💶 Fatture con bolle da prezzare</div>{daPrezzare.map(x => <div key={x.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0" }}><span style={{ flex: 1 }}>{x.nome}</span><button style={{ ...st.btn("primario"), padding: "5px 10px", fontSize: 13 }} onClick={() => setPrezzi(x.id)}>Controlla</button><button style={{ ...st.btn(), padding: "5px 9px", fontSize: 12 }} onClick={() => setDaPrezzare(q => q.filter(y => y.id !== x.id))}>✕</button></div>)}</div>}
      {errori.length > 0 && <div style={{ ...st.card, borderColor: C.err, color: C.err, fontSize: 13 }}>{errori.map((e, i) => <div key={i}>⚠️ {e}</div>)}</div>}

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {tb("arrivo", "Da confermare" + (attesa.length ? " (" + attesa.length + ")" : ""))}
        {tb("archivio", "Archivio")}
        {tb("riepilogo", "Riepilogo")}
        {tb("banca", "🏦 Banca")}
        {tb("vendite", "🧾 Vendite")}
      </div>

      {tab === "arrivo" && (
        <div>
          {attesa.length === 0 && !coda && <div style={{ color: C.muted, padding: 10 }}>Niente da confermare. Carica dei documenti qui sopra.</div>}
          {attesa.length > 1 && <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {massa ? <span style={{ color: C.accent, fontWeight: 700 }}>⏳ {massa.testo} {massa.n} di {massa.tot}… lascia la pagina aperta</span>
              : chiedi ? <>
                <span style={{ fontWeight: 600 }}>{chiedi === "conferma" ? "Registrare le " + attesa.filter(pronta).length + " schede pronte così come sono proposte?" : "Scartare le " + nDop + " schede che sembrano doppioni?"}</span>
                <button style={st.btn("primario")} onClick={chiedi === "conferma" ? confermaInBlocco : scartaDoppioni}>Sì</button>
                <button style={st.btn()} onClick={() => setChiedi("")}>No</button>
              </> : <>
                <button style={st.btn()} onClick={() => setChiedi("conferma")}>✓ Conferma tutte ({attesa.filter(pronta).length} pronte di {attesa.length})</button>
                {nDop > 0 && <button style={st.btn()} onClick={() => setChiedi("doppioni")}>🗑 Scarta i doppioni ({nDop})</button>}
              </>}
          </div>}
          {attesa.map(s => <SchedaAttesa key={s.id} s={s} categorie={categorie} cantieri={cantieriDB} lavoratori={lavoratori} segnale={segnale} onFatto={(esito, j) => fatto(s.id, esito, j)} />)}
        </div>
      )}

      {tab === "archivio" && (
        <div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <select style={{ ...st.inp, width: "auto" }} value={anno} onChange={e => setAnno(e.target.value)}>{anni.map(a => <option key={a}>{a}</option>)}</select>
            <select style={{ ...st.inp, width: "auto" }} value={filtroMese} onChange={e => setFiltroMese(e.target.value)}>
              <option value="">Tutti i mesi</option>{MESI_LUNGHI.map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
            </select>
            <select style={{ ...st.inp, width: "auto", maxWidth: 260 }} value={filtroCat} onChange={e => setFiltroCat(e.target.value)}>
              <option value="">Tutte le categorie</option>{categorie.map(c => <option key={c}>{c}</option>)}
            </select>
            <input style={{ ...st.inp, flex: 1, minWidth: 160 }} placeholder="Cerca fornitore, lavoratore, numero, cantiere…" value={cerca} onChange={e => setCerca(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: C.muted, flex: 1 }}>{filtrati.length} documenti · {euro(filtrati.filter(x => !NON_COSTO.includes(x.categoria)).reduce((s, x) => s + valore(x), 0))} senza IVA</span>
            {filtrati.some(x => x.files && x.files.length) && <button style={{ ...st.btn(), fontSize: 13, padding: "6px 10px" }} onClick={() => { const n = {}; filtrati.forEach(x => { if (x.files && x.files.length) n[x.id] = true; }); setScelti(n); }}>Seleziona tutti</button>}
            {listaScelti.length > 0 && <button style={st.btn("primario")} onClick={() => setInvio({ docs: listaScelti, preset: {} })}>📤 Invia selezionati ({listaScelti.length})</button>}
          </div>
          {filtrati.map(x => (
            <div key={x.id} style={{ ...st.card, padding: "12px 14px", marginBottom: 8, borderColor: scelti[x.id] ? C.accent : C.border }}>
              <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                {x.files && x.files.length > 0
                  ? <input type="checkbox" checked={!!scelti[x.id]} onChange={() => setScelti(sc => { const n = { ...sc }; if (n[x.id]) delete n[x.id]; else n[x.id] = true; return n; })} style={{ width: 18, height: 18, accentColor: C.accent, alignSelf: "center" }} />
                  : <span style={{ width: 18 }} />}
                <span style={{ fontSize: 13, color: C.muted, minWidth: 82 }}>{dataIt(x.data)}</span>
                <span style={{ flex: 1, fontWeight: 700, minWidth: 140 }}>{x.destinazione === "lavoratore" ? x.lavoratoreNome : x.fornitore}</span>
                <span style={{ fontWeight: 700 }}>{euro(x.totale)}</span>
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 3, paddingLeft: 28 }}>
                {x.tipo}{x.numero ? " n. " + x.numero : ""}{x.descrizione ? " · " + x.descrizione : ""}{x.destinazione === "lavoratore" && x.mese ? " · " + meseIt(x.mese) : ""}
                {x.oreOrdinarie != null ? " · " + x.oreOrdinarie + " ore" + (x.oreStraordinarie ? " + " + x.oreStraordinarie + " straord." : "") : ""}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8, paddingLeft: 28 }}>
                <select style={{ ...st.inp, width: "auto", maxWidth: 240, padding: "5px 8px", fontSize: 13 }} value={x.categoria} onChange={e => cambiaCategoria(x, e.target.value)}>
                  {categorie.map(c => <option key={c}>{c}</option>)}
                </select>
                <span style={{ fontSize: 12, color: x.destinazione === "cantiere" ? C.accent : C.muted }}>
                  {x.destinazione === "cantiere" ? "🏗️ " + (x.cantiere || nomeCant(x.cantiereId)) : x.destinazione === "lavoratore" ? "👷 cartella del lavoratore" : DEST[x.destinazione] || x.destinazione}
                </span>
                {x.pagato && <span style={{ fontSize: 12, color: C.ok }}>✓ pagato</span>}
                <span style={{ flex: 1 }} />
                {x.soloDati && <span style={{ fontSize: 12, color: C.dim }}>🧾 su Tieni il Conto</span>}
                {(x.files || []).map((f, i) => <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" style={{ ...st.btn(), padding: "5px 10px", fontSize: 12 }}>📄 Apri</a>)}
                {x.destinazione === "lavoratore" && x.files && x.files.length > 0 && <button style={{ ...st.btn(), padding: "5px 10px", fontSize: 12 }} onClick={() => setInvio({ docs: [x], preset: { per: x.lavoratoreNome, giorni: 7, lavoratore: true, nota: (x.descrizione || x.tipo) + (x.mese ? " di " + meseIt(x.mese) : "") } })}>📤 Al lavoratore</button>}
                {(x.ddt || []).length > 0 && <button onClick={() => setPrezzi(x.id)} style={{ ...st.btn(), padding: "5px 10px", fontSize: 12 }}>💶 Prezzi bolle{x.prezziBolle ? " ✓" : ""}</button>}
                <button title="Elimina" onClick={() => elimina(x)} style={{ ...st.btn(), padding: "5px 9px", fontSize: 12, color: C.muted }}>🗑</button>
              </div>
            </div>
          ))}
          {filtrati.length === 0 && <div style={{ color: C.muted, padding: 10 }}>Nessun documento per questa ricerca.</div>}
        </div>
      )}

      {tab === "riepilogo" && (
        <div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <select style={{ ...st.inp, width: "auto" }} value={anno} onChange={e => setAnno(e.target.value)}>{anni.map(a => <option key={a}>{a}</option>)}</select>
            <span style={{ fontSize: 13, color: C.muted }}>Importi senza IVA, per mese della data del documento (gli stipendi nel mese della busta).</span>
          </div>
          <div style={{ ...st.card, overflowX: "auto", padding: 0 }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13, minWidth: 900 }}>
              <thead>
                <tr style={{ background: C.surface2 }}>
                  <th style={{ textAlign: "left", padding: "8px 10px", position: "sticky", left: 0, background: C.surface2 }}>Spese per categoria</th>
                  {MESI.map(m => <th key={m} style={{ textAlign: "right", padding: "8px 6px", color: C.muted, fontWeight: 600 }}>{m}</th>)}
                  <th style={{ textAlign: "right", padding: "8px 10px" }}>Totale</th>
                </tr>
              </thead>
              <tbody>
                {catCosti.map(k => (
                  <tr key={k} style={{ borderTop: "1px solid " + C.border }}>
                    <td style={{ padding: "7px 10px", position: "sticky", left: 0, background: C.surface }}>{k}{righeRiep[k].doppio > 0 ? <span style={{ color: C.dim, fontSize: 11 }}> · in parte già nei cantieri</span> : null}</td>
                    {righeRiep[k].mesi.map((v, i) => <td key={i} style={{ ...td, color: v ? C.text : C.dim }}>{n0(v)}</td>)}
                    <td style={{ ...td, padding: "7px 10px", fontWeight: 700 }}>{n0(righeRiep[k].tot)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: "2px solid " + C.border, background: C.surface2 }}>
                  <td style={{ padding: "8px 10px", fontWeight: 700, position: "sticky", left: 0, background: C.surface2 }}>Totale spese</td>
                  {totMesi.map((v, i) => <td key={i} style={{ ...td, fontWeight: 700 }}>{n0(v)}</td>)}
                  <td style={{ ...td, padding: "8px 10px", fontWeight: 800 }}>{n0(totAnno)}</td>
                </tr>
                <tr style={{ borderTop: "1px solid " + C.border }}>
                  <td style={{ padding: "8px 10px", fontWeight: 700, color: C.ok, position: "sticky", left: 0, background: C.surface }}>Incassi dai clienti</td>
                  {incMesi.map((v, i) => <td key={i} style={{ ...td, color: v ? C.ok : C.dim }}>{n0(v)}</td>)}
                  <td style={{ ...td, padding: "8px 10px", fontWeight: 800, color: C.ok }}>{n0(incAnno)}</td>
                </tr>
                <tr style={{ borderTop: "1px solid " + C.border, background: C.surface2 }}>
                  <td style={{ padding: "8px 10px", fontWeight: 700, position: "sticky", left: 0, background: C.surface2 }}>Incassi − spese</td>
                  {incMesi.map((v, i) => { const x = v - totMesi[i]; return <td key={i} style={{ ...td, fontWeight: 700, color: x > 0 ? C.ok : x < 0 ? C.err : C.dim }}>{n0(x)}</td>; })}
                  <td style={{ ...td, padding: "8px 10px", fontWeight: 800, color: incAnno - totAnno >= 0 ? C.ok : C.err }}>{n0(incAnno - totAnno)}</td>
                </tr>
                {catAltre.map(k => (
                  <tr key={k} style={{ borderTop: "1px solid " + C.border, color: C.dim }}>
                    <td style={{ padding: "7px 10px", position: "sticky", left: 0, background: C.surface }}>{k}</td>
                    {righeRiep[k].mesi.map((v, i) => <td key={i} style={td}>{n0(v)}</td>)}
                    <td style={{ ...td, padding: "7px 10px" }}>{n0(righeRiep[k].tot)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ ...st.card, fontSize: 14, lineHeight: 1.6 }}>
            Spese registrate nel {anno}: <b>{euro(totAnno)}</b> senza IVA · incassi: <b style={{ color: C.ok }}>{euro(incAnno)}</b> senza IVA.<br />
            Spese <b>non già contate nei cantieri tramite le bolle</b>: <b style={{ color: C.accent }}>{euro(totGenerali)}</b> — è la base per il costo orario di gestione e il punto di pareggio.
            <div style={{ fontSize: 12, color: C.dim, marginTop: 6 }}>Il quadro è vero solo quando ci sono tutti i documenti del periodo: fatture, F24, buste paga, Cassa edile. Gli incassi sono quelli segnati nei pagamenti dei cantieri.</div>
          </div>
        </div>
      )}
      {tab === "banca" && <BancaMovimenti cantieriDB={cantieriDB} />}
      {tab === "vendite" && <FattureVendita cantieriDB={cantieriDB} />}
      {prezzi && <PrezziBolle docId={prezzi} onClose={() => { setDaPrezzare(q => q.filter(y => y.id !== prezzi)); setPrezzi(null); api("/api/cassetta/documenti").then(d => setDocumenti(d.documenti || [])).catch(() => {}); }} />}
      {invio && <FinestraInvio docs={invio.docs} preset={invio.preset} onClose={() => { setInvio(null); setScelti({}); }} />}
    </div>
  );
}
